import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '../../environments/environment';

export const authGuard: CanActivateFn = async () => {
  // Mock authentication always allows access
  if (environment.useMockAuth) {
    console.log('🔓 Mock Auth: Route access granted');
    return true;
  }

  const authService = inject(MsalService);
  const router = inject(Router);

  const accounts = authService.instance.getAllAccounts();

  // No cached account at all: send to the login page (which triggers the interactive flow). Navigating
  // rather than calling loginRedirect here avoids racing the MSAL interceptor's own interactive attempts.
  if (accounts.length === 0) {
    router.navigate(['/login']);
    return false;
  }

  const account = authService.instance.getActiveAccount() ?? accounts[0];
  authService.instance.setActiveAccount(account);

  // A cached account can outlive the Entra sign-in session: localStorage still holds the account (and
  // stale tokens), but the server-side session is gone, so silent renewal fails with
  // `AADSTS160021: Application requested a user session which does not exist` (interaction_required).
  // Merely checking `accounts.length > 0` let the app load in that state — the guard said "authenticated"
  // while every API call silently died (the MSAL interceptor could never attach a token, so GraphQL
  // requests were never sent) and SignalR negotiated with no bearer (401 loop). So actually prove we can
  // mint an API token before activating the route; if interaction is required, force an interactive
  // sign-in to re-establish the session instead of entering that zombie state.
  try {
    await authService.instance.acquireTokenSilent({
      account,
      scopes: environment.apiConfig.scopes,
    });
    return true;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Full-page redirect to re-authenticate; the current navigation is abandoned (we return false) and
      // app.component's handleRedirectPromise routes home once the redirect completes.
      await authService.instance.loginRedirect({
        scopes: environment.apiConfig.scopes,
        account,
      });
      return false;
    }
    // Transient, non-interaction failure (e.g. a network blip): don't bounce the user to login. Let the
    // route activate; the MSAL interceptor will retry token acquisition on the next request.
    console.warn('authGuard: silent token check failed (non-interactive); allowing navigation.', error);
    return true;
  }
};
